/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import type { FormikProps } from 'formik';
import type { CountryData } from 'react-intl-tel-input';
import IntlTelInput from 'react-intl-tel-input';
import { TextV2, FormControl, InputV2, SpacingStack, Divider, Flex, Box } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import type { Contacts, ContactInputFields } from '@apps/guest/contactCollector/shared/types';
import type { IframeContacts, IframeContactInputFields } from '@apps/guest/contactCollector/shared/types';
import { StyledFormControl, StyledContainer } from './ContactInfo.styles';
import { RemoveMember } from '../RemoveMember/RemoveMember';
import 'react-intl-tel-input/dist/main.css';
import type { FieldVisibility } from '../../Form';
import { IntlTelInputWrapper } from '@shared/components/IntlPhoneNumber/IntlTelInputWrapper';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';

interface ContactInfoProps
  extends Readonly<{
    formik: FormikProps<Contacts> | FormikProps<IframeContacts>;
    index: number;
    errors: {
      firstNameError: (index: number) => any;
      lastNameError: (index: number) => any;
      emailError: (index: number) => any;
    };
    isPhoneNumberInvalid: (index: number) => boolean;
    isEmailInvalid: (index: number) => boolean;
    handleOnPhoneNumberChange: (isValid: boolean, value: string, selectedCountryData: CountryData, fullNumber: string, extension: string, index: number) => void;
    handleOnPhoneNumberFocus: (isValid: boolean, value: string, selectedCountryData: CountryData, fullNumber: string, extension: string, index: number) => void;
    remove: (index: number) => ContactInputFields | IframeContactInputFields | undefined;
    isIframeVersion?: boolean;
    formatIntlTelInputOnInit?: boolean;
    phoneNumberRule?: FieldVisibility;
    isRegistration?: boolean;
    isPhoneNumberInvalidWithoutFocus: (index: number) => boolean;
    setIsPhoneNumberValid: React.Dispatch<React.SetStateAction<{ [key: number]: boolean }>>;
    phoneNumberTouched: { [key: number]: boolean };
    setPhoneNumberTouched: React.Dispatch<React.SetStateAction<{ [key: number]: boolean }>>;
    phoneInputResetKey?: number;
  }> {}

export const ContactInfo: React.FC<ContactInfoProps> = ({
  formik,
  index,
  errors: { firstNameError, lastNameError, emailError },
  isPhoneNumberInvalid,
  isEmailInvalid,
  handleOnPhoneNumberChange,
  handleOnPhoneNumberFocus,
  remove,
  isIframeVersion = false,
  formatIntlTelInputOnInit = true,
  phoneNumberRule = 'optional',
  isRegistration = false,
  isPhoneNumberInvalidWithoutFocus,
  setIsPhoneNumberValid,
  phoneNumberTouched,
  setPhoneNumberTouched,
  phoneInputResetKey,
}) => {
  const { t, tq } = useTranslation('contactCollector');
  const tAdmin = t('admin');
  const showRequiredMark = isIframeVersion && index === 0;
  const [intlTelInputEnabled] = useIsFeatureEnabled('intlTelInputEnabled');

  return (
    <StyledContainer spacing={6}>
      {index === 0 && (
        <TextV2
          typographyVariant="hed4"
          marginTop="2rem"
          width="100%"
        >
          {tAdmin.contactInfo()}
        </TextV2>
      )}
      {index > 0 && <Divider marginTop="2rem" />}
      <Flex
        justifyContent="flex-start"
        columnGap="10px"
        marginTop={index > 0 ? '24px' : 0}
      >
        {index > 0 && (
          <Box paddingTop="30px">
            <RemoveMember
              formik={formik}
              remove={remove}
              index={index}
            />
          </Box>
        )}
        <SpacingStack
          spacing={6}
          width="100%"
        >
          <FormControl
            label={showRequiredMark ? tq`First Name*` : tq`First Name`}
            isInvalid={!!firstNameError(index)}
            error={firstNameError(index)}
            isRequired
          >
            <InputV2
              {...formik.getFieldProps(`contacts[${index}].firstName`)}
              autoComplete="given-name"
            />
          </FormControl>
          <FormControl
            label={showRequiredMark ? tq`Last Name*` : tq`Last Name`}
            isInvalid={!!lastNameError(index)}
            error={lastNameError(index)}
            isRequired
          >
            <InputV2
              {...formik.getFieldProps(`contacts[${index}].lastName`)}
              autoComplete="family-name"
            />
          </FormControl>
          {phoneNumberRule !== 'hidden' &&
            (intlTelInputEnabled ? (
              <StyledFormControl
                label={showRequiredMark && phoneNumberRule === 'required' ? tq`Mobile Phone*` : tq`Mobile Phone`}
                isInvalid={isPhoneNumberInvalidWithoutFocus(index)}
                error={isPhoneNumberInvalidWithoutFocus(index) ? tAdmin.invalidPhoneNumber() : ''}
                isRequired={phoneNumberRule === 'required'}
              >
                <IntlTelInputWrapper
                  resetKey={phoneInputResetKey}
                  initialValue={formik.getFieldProps(`contacts[${index}].phoneNumber`).value}
                  onChangeNumber={number => formik.setFieldValue(`contacts[${index}].phoneNumber`, number)}
                  onChangeValidity={valid =>
                    setIsPhoneNumberValid(prevState => ({
                      ...prevState,
                      [index]: valid,
                    }))
                  }
                  initOptions={{ initialCountry: 'us', countryOrder: ['us', 'ca', 'gb', 'au'], formatOnDisplay: true, strictMode: true, useFullscreenPopup: false }}
                  inputProps={{
                    autoComplete: 'tel',
                    onBlur: () => {
                      if (!phoneNumberTouched[index]) {
                        setPhoneNumberTouched(prevState => ({ ...prevState, [index]: true }));
                      }
                    },
                  }}
                />
              </StyledFormControl>
            ) : (
              <StyledFormControl
                label={showRequiredMark && phoneNumberRule === 'required' ? tq`Mobile Phone*` : tq`Mobile Phone`}
                isInvalid={isPhoneNumberInvalid(index)}
                error={isPhoneNumberInvalid(index) ? tAdmin.invalidPhoneNumber() : ''}
                isRequired={phoneNumberRule === 'required'}
              >
                <IntlTelInput
                  {...formik.getFieldProps(`contacts[${index}].phoneNumber`)}
                  autoComplete="tel"
                  preferredCountries={['us']}
                  onPhoneNumberChange={(isValid: boolean, value: string, selectedCountryData: CountryData, fullNumber: string, extension: string) =>
                    handleOnPhoneNumberChange(isValid, value, selectedCountryData, fullNumber, extension, index)
                  }
                  onPhoneNumberFocus={(isValid: boolean, value: string, selectedCountryData: CountryData, fullNumber: string, extension: string) =>
                    handleOnPhoneNumberFocus(isValid, value, selectedCountryData, fullNumber, extension, index)
                  }
                  formatOnInit={formatIntlTelInputOnInit}
                />
              </StyledFormControl>
            ))}
          <FormControl
            label={isRegistration ? tq`Email*` : tq`Email`}
            isInvalid={(!isIframeVersion && isEmailInvalid(index)) || !!emailError(index)}
            error={!isIframeVersion && isEmailInvalid(index) ? tAdmin.required() : emailError(index) ? emailError(index) : ''}
            isRequired={!isIframeVersion && index === 0}
          >
            <InputV2
              {...formik.getFieldProps(`contacts[${index}].email`)}
              placeholder={tq`your@email.com`}
              autoComplete="email"
            />
          </FormControl>
        </SpacingStack>
      </Flex>
    </StyledContainer>
  );
};
